<script setup lang="ts">
import type { PropType } from 'vue';
import { differenceInMilliseconds, parseISO } from 'date-fns';
import CollectionAttributeFilter from '~/components/collections/CollectionAttributeFilter.vue';
import { useInfiniteScroll } from '~/composables/infinite-scroll';
import { getApiRoot } from '~/utilities/api';

const props = defineProps({
  collection: { type: Object as PropType<any>, required: true },
  gridStyle: { type: String as PropType<'grid-compact' | 'grid' | 'table'>, required: true },
  sorting: {
    type: String as PropType<'xch_price' | 'created_at' | '-created_at' | '-updated_at' | 'rank'>,
    default: 'xch_price',
  },
  attributeFilters: { type: Array as PropType<{ name: string; value: string }[]>, default: () => [] },
  tokenId: { type: String, default: 'xch' },
});
const { collection, gridStyle, sorting, tokenId } = toRefs(props);

const emit = defineEmits(['filterChanged']);

const attributeFilters = ref<{ name: string; value: string }[]>(props.attributeFilters);

const filterQuery = computed(() => {
  return attributeFilters.value.length > 0
    ? '&' +
        attributeFilters.value
          .map((filter) => `filter=${encodeURIComponent(filter.name)}&filter_value=${encodeURIComponent(filter.value)}`)
          .join('&')
    : '';
});

watch(
  attributeFilters,
  (updatedFilters) => {
    emit('filterChanged', updatedFilters);
  },
  { deep: true }
);

const { data: auctions } = useLazyFetch(`${getApiRoot()}/collections/${collection.value.id}/auctions`);

const bottom = ref();
const url = () => {
  if (['xch_price', '-created_at'].includes(sorting.value)) {
    return `${getApiRoot()}/collections/${collection.value.id}/nfts/by_offers?token_id=${tokenId.value}&sort_by=${
      sorting.value
    }${filterQuery.value}`;
  } else {
    return `${getApiRoot()}/collections/${collection.value.id}/nfts?sort_by=${sorting.value}${filterQuery.value}`;
  }
};
const { items: nfts, pending } = useInfiniteScroll(bottom, url, [sorting, filterQuery, tokenId]);

const allNfts = computed(() => {
  const auctionNfts = (auctions.value?.items || [])
    .filter(
      (auction) =>
        differenceInMilliseconds(parseISO(auction.start), new Date()) < 0 &&
        differenceInMilliseconds(parseISO(auction.overtime.end), new Date()) > 0
    )
    .map((auction) => ({ ...auction.nft, auction }));
  return [...auctionNfts, ...nfts.value.filter((nft) => !auctionNfts.find(({ id }) => id === nft.id))];
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-x-8 lg:flex-row">
      <CollectionAttributeFilter
        v-if="collection?.attributes?.length > 0"
        v-model="attributeFilters"
        :collection="collection"
      />
      <div class="mt-6 flex-1 lg:mt-0">
        <ExploreGrid :nfts="allNfts" :pending="pending" :grid-style="gridStyle" />
        <div ref="bottom"></div>
      </div>
    </div>
  </div>
</template>
